import { getConsent } from './consent'
import { getLocale } from './i18n'

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}

let initialized = false
const onInitCallbacks: (() => void)[] = []

export function onGAReady(cb: () => void): void {
  if (initialized) { cb(); return }
  onInitCallbacks.push(cb)
}
const DEBUG = import.meta.env.DEV

function getMeasurementId(): string | undefined {
  return (
    (window as Window & { ENV?: Record<string, string> }).ENV
      ?.VITE_GA_MEASUREMENT_ID || import.meta.env.VITE_GA_MEASUREMENT_ID
  )
}

function getGtmId(): string | undefined {
  return (
    (window as Window & { ENV?: Record<string, string> }).ENV?.VITE_GTM_ID ||
    import.meta.env.VITE_GTM_ID
  )
}

function log(action: string, ...args: unknown[]) {
  if (!DEBUG) return
  console.log(`%c[GA] ${action}`, 'color: #FF6B47; font-weight: bold', ...args)
}

// --- Consent Mode v2 ---
// Must be called before gtag('config') so Google knows the default consent state.
// Even when consent is denied, Google can do cookieless pings + conversion modeling.
function bootstrapGtag() {
  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments)
  }
}

// 광고 신호 3종은 분석 동의와 같은 배너 한 번으로 함께 결정된다. 이 셋이 denied 인 채로
// 광고 태그가 발화하면 Google Ads 전환 매칭·리마케팅 적재·향상된 전환이 전부 동작하지 않는다.
const AD_SIGNALS = ['ad_storage', 'ad_user_data', 'ad_personalization'] as const

function consentSignals(value: 'granted' | 'denied') {
  return {
    analytics_storage: value,
    ...Object.fromEntries(AD_SIGNALS.map((k) => [k, value])),
  }
}

export function setupConsentDefaults(): void {
  bootstrapGtag()
  const prior = getConsent()
  const granted = prior === 'accepted' ? 'granted' : 'denied'
  window.gtag('consent', 'default', {
    ...consentSignals(granted),
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500,
  })
  log('consent default', granted)
}

export function updateConsent(accepted: boolean): void {
  bootstrapGtag()
  const value = accepted ? 'granted' : 'denied'
  window.gtag('consent', 'update', consentSignals(value))
  log('consent update', value)
  if (accepted) {
    initGA()
    initGTM()
  }
}

// --- GA init ---

export function initGA(): void {
  if (initialized) return
  const id = getMeasurementId()
  if (!id) return
  if (getConsent() !== 'accepted') return

  bootstrapGtag()
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`
  document.head.appendChild(script)

  window.gtag('js', new Date())
  window.gtag('config', id, {
    send_page_view: false,
    debug_mode: DEBUG,
  })

  initialized = true
  log('initialized', id)
  // 세션 도중 동의한 경우에만 여기서 page_view 를 쏜다.
  //
  // usePageTracking 의 effect 는 GA 초기화만으로 다시 돌지 않으므로, 배너에서 수락한
  // 그 페이지가 통째로 누락된다 — 그래서 이 보정이 있다. 그런데 이미 동의한 재방문자는
  // main.tsx 의 initGA 가 React 마운트보다 먼저 도는 탓에 effect 와 겹쳐 page_view 가
  // 두 번 나갔다(실측). 트래픽 대부분이 재방문이라 페이지뷰가 2배로 부풀었다.
  if (pageTrackingMounted) {
    trackPageView(window.location.pathname, window.location.search || undefined)
  }
  onInitCallbacks.forEach((cb) => cb())
  onInitCallbacks.length = 0
}

// --- Google Tag Manager ---
//
// 마케팅이 코드 배포 없이 태그·트리거를 붙이는 통로 — 광고 태그(Google Ads·LinkedIn·
// Naver)와 DOM 기반 이벤트 계측(클릭·스크롤·노출) 둘 다 여기서 처리한다.
//
// 경계는 하나뿐이다: **컨테이너에 GA4 "구성" 태그를 넣으면 안 된다.** GA4 로딩은 위
// initGA 가 소유하므로, GTM 에도 두면 page_view 가 이중 계측된다. 마찬가지로 앱이
// trackEvent 로 이미 쏘는 이벤트를 GTM 에서 또 잡으면 중복이다 (예: early_access_cta_click).
//
// 컨테이너 ID 는 환경별로 다르다(dev/prod 분리). docker/entrypoint.sh 가 VITE_GTM_ID 로
// 주입하며, 비어 있으면 로드하지 않는다. GA 와 같은 동의 게이트를 탄다 — 위 consent
// default/update 가 dataLayer 에 먼저 쌓이므로 GTM 은 로드 즉시 현재 동의 상태를 본다.

let gtmInitialized = false

export function initGTM(): void {
  if (gtmInitialized) return
  const id = getGtmId()
  if (!id) return
  if (getConsent() !== 'accepted') return

  bootstrapGtag()
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${id}`
  document.head.appendChild(script)

  gtmInitialized = true
  log('gtm initialized', id)
}

export function isGTMInitialized(): boolean {
  return gtmInitialized
}

// --- User identity ---

export function setUserId(userId: string): void {
  if (!initialized) return
  window.gtag('set', { user_id: userId })
  log('set user_id', userId)
}

export function clearUserId(): void {
  if (!initialized) return
  window.gtag('set', { user_id: null })
  log('clear user_id')
}

export function setUserProperties(props: Record<string, unknown>): void {
  if (!initialized) return
  window.gtag('set', 'user_properties', props)
  log('set user_properties', props)
}

// --- Page view ---

const CONTENT_GROUPS: Record<string, string> = {
  '/': 'public',
  '/login': 'public',
  '/signup': 'public',
  '/terms': 'public',
  '/privacy': 'public',
  '/api-doc': 'public',
  '/use-cases': 'public',
  '/en': 'public',
  '/try': 'public',
  '/early-access': 'public',
  '/widget': 'widget',
  '/dashboard': 'app',
  '/agents': 'app',
  '/sessions': 'app',
  '/keys': 'app',
  '/knowledge': 'app',
  '/pricing': 'app',
  '/account': 'app',
}

function getContentGroup(path: string): string {
  if (CONTENT_GROUPS[path]) return CONTENT_GROUPS[path]
  for (const [prefix, group] of Object.entries(CONTENT_GROUPS)) {
    if (path.startsWith(prefix + '/')) return group
  }
  return 'other'
}

/**
 * 이벤트 정의 시트("[Neona Agent] Events")의 `page` 파라미터.
 *
 * 정적 랜딩(`landing-overlay/assets/analytics.js` 의 pageId)과 **같은 규칙**을 쓴다 —
 * 두 표면이 하나의 GA4 속성으로 들어오므로 값이 갈리면 같은 페이지가 둘로 쪼개진다.
 * 한쪽을 고치면 다른 쪽도 같이 고쳐야 한다.
 *
 * 시트 정의: home · industry_cx · industry_edu · usecases · voices · uc_{슬러그}
 */
export function pageId(path: string = window.location.pathname): string {
  const p = scrubPath(path).replace(/^\//, '').replace(/\.html$/, '').replace(/\/$/, '')
  if (p === '' || p === 'index') return 'home'
  if (p === 'aicc') return 'industry_cx'
  if (p === 'edu') return 'industry_edu'
  if (p === 'voices') return 'voices'
  if (p === 'contact') return 'contact'
  const staticDetail = p.match(/^usecase-(.+)$/)
  if (staticDetail) return `uc_${staticDetail[1]}`
  const reactDetail = p.replace(/^en\//, '').match(/^use-cases\/(.+)$/)
  if (reactDetail) return `uc_${reactDetail[1]}`
  if (p === 'usecases' || /^(en\/)?use-cases$/.test(p)) return 'usecases'
  return p || 'other'
}

/**
 * page_view 시점의 표시 언어.
 *
 * initGA 의 첫 page_view 는 React 마운트보다 먼저 나가므로 LocaleSync 가 아직 URL 을
 * 반영하지 않았다 — getLocale() 만 믿으면 /en/* 페이지가 ko 로 잡힌다(실측 확인).
 * LocaleSync 와 같은 규칙으로 경로에서 직접 읽고, 마케팅 경로가 아니면 저장 선호를 쓴다.
 */
function localeForPath(path: string): string {
  if (path === '/en' || path.startsWith('/en/')) return 'en'
  return getLocale()
}

/**
 * usePageTracking 이 마운트됐는가. initGA 의 보정 발화가 effect 와 겹치지 않게 하는 게이트다
 * (위 initGA 주석 참조). 정적 랜딩에는 이 훅이 없어 항상 false 로 남는다 — 랜딩은 gtag
 * config 가 page_view 를 자동 발화하므로 애초에 보정이 필요 없다.
 */
let pageTrackingMounted = false

export function markPageTrackingMounted(): void {
  pageTrackingMounted = true
}

/**
 * URL 에서 자격증명 성격의 쿼리를 걷어낸다.
 *
 * page_location 은 window.location.href 원문이었다 — /signup?invite=… 의 초대 토큰은
 * **단회용 자격증명**이라 GA4 에 적재되는 순간 그 자체로 유출이다(실측으로 확인).
 * usePageTracking 이 search 만 정제하고 있어서 href 쪽으로 그대로 빠져나갔다.
 */
// team_invite — 팀원 초대 링크(/signup?team_invite=…)의 단회용 토큰(TASK-16726)
const SENSITIVE_PARAMS = ['invite', 'team_invite', 'token', 'code', 'rt', 'key', 'secret', 'password', 'access_token', 'id_token']

/** 값이 URL 이면 그 안의 민감 쿼리까지 걷어낸다. */
function scrubValue(value: unknown): unknown {
  if (typeof value !== 'string') return value
  if (!/^https?:\/\//.test(value) && !value.includes('?')) return value
  return scrubUrl(value)
}

/**
 * GA 로 나가는 모든 파라미터의 마지막 관문.
 *
 * 이 프로젝트에서 **초대 토큰이 page_location 을 통해 GA4 로 나간 사고**가 있었다.
 * usePageTracking 이 search 를 정제하고 있었는데도, 같은 값이 들어 있는 href 는
 * 그대로 실려 나갔다 — 정제가 호출부마다 흩어져 있으면 새 파라미터가 추가될 때마다
 * 같은 사고가 반복된다. 그래서 전송 직전 한 곳에서 전부 훑는다.
 *
 * 새 파라미터를 추가할 때 여기를 신경 쓸 필요는 없다. 그게 이 함수의 목적이다.
 */
function scrubParams(params?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!params) return params
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(params)) {
    out[k] = SENSITIVE_PARAMS.includes(k) ? '[redacted]' : scrubValue(v)
  }
  return out
}

/**
 * 자격증명이 **경로**에 들어가는 라우트.
 *
 * 쿼리만 막으면 부족하다 — /try/<token> 은 공유 링크 토큰이 경로 세그먼트다.
 * page·page_path·page_location 세 곳 모두 같은 정제를 지나야 한다(CodeRabbit 지적).
 */
const CREDENTIAL_PATHS: [RegExp, string][] = [
  [/^\/try\/[^/]+/, '/try/:token'],
  [/^\/(en\/)?early-access\/revoke\/[^/]+/, '/early-access/revoke/:token'],
]

export function scrubPath(path: string): string {
  for (const [re, safe] of CREDENTIAL_PATHS) {
    if (re.test(path)) return path.replace(re, safe)
  }
  return path
}

function scrubUrl(href: string): string {
  try {
    // 상대 경로(/signup?token=…)도 정제 대상이다 — base 없이 new URL 하면 던지고,
    // 예전 구현은 그 경우 원문을 그대로 돌려줘 조용히 새어 나갔다(테스트로 확인).
    const isAbsolute = /^https?:\/\//.test(href)
    const u = new URL(href, isAbsolute ? undefined : 'http://scrub.invalid')
    let touched = false
    SENSITIVE_PARAMS.forEach((k) => {
      if (u.searchParams.has(k)) { u.searchParams.delete(k); touched = true }
    })
    const safePath = scrubPath(u.pathname)
    if (safePath !== u.pathname) { u.pathname = safePath; touched = true }
    // 프래그먼트도 버린다 — 마켓플레이스 등록은 #rt=<구매증명> 으로 온다.
    if (u.hash) { u.hash = ''; touched = true }
    if (!touched) return href
    return isAbsolute ? u.toString() : `${u.pathname}${u.search}`
  } catch {
    return href
  }
}

function scrubSearch(search?: string): string | undefined {
  if (!search) return undefined
  const q = new URLSearchParams(search)
  SENSITIVE_PARAMS.forEach((k) => q.delete(k))
  return q.toString() || undefined
}

export function trackPageView(path: string, search?: string): void {
  if (!initialized) return
  const params: Record<string, unknown> = {
    page: pageId(path),
    locale: localeForPath(path),
    page_path: scrubPath(path),
    page_title: document.title,
    page_location: scrubUrl(window.location.href),
    content_group: getContentGroup(path),
  }
  const cleanSearch = scrubSearch(search)
  if (cleanSearch) params.page_referrer_search = cleanSearch
  const safe = scrubParams(params)!
  window.gtag('event', 'page_view', safe)
  log('page_view', path, safe)
}

// --- Events ---

export function trackEvent(
  name: string,
  params?: Record<string, unknown>,
): void {
  if (!initialized) return
  const safe = scrubParams(params)
  window.gtag('event', name, safe)
  log('event', name, safe)
}

/**
 * 정적 랜딩 번들(src/landing/*)용 전송구.
 *
 * 랜딩은 React 앱과 별도 엔트리라 initGA 를 타지 않는다(동의·gtag 로드는
 * landing-overlay/assets/analytics.js 가 담당). 그래서 initialized 대신 동의 상태를
 * 직접 보되, **파라미터 정제는 앱과 같은 scrubParams 를 지난다** — 직접 gtag 를 부르면
 * 그 검사를 통째로 우회한다.
 */
export function sendLandingEvent(name: string, params?: Record<string, unknown>): void {
  try {
    if (getConsent() !== 'accepted') return
  } catch {
    return
  }
  const gtag = (globalThis as { gtag?: (...args: unknown[]) => void }).gtag
  if (typeof gtag !== 'function') return
  const safe = scrubParams(params)
  gtag('event', name, safe)
  log('landing event', name, safe)
}

// --- Outbound link tracking ---

let outboundListenerAttached = false

export function attachOutboundLinkTracking(): void {
  if (outboundListenerAttached) return
  outboundListenerAttached = true

  document.addEventListener('click', (e) => {
    const anchor = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null
    if (!anchor) return
    const url = anchor.href
    if (!url || anchor.hostname === window.location.hostname) return

    trackEvent('outbound_click', {
      url,
      link_text: (anchor.textContent || '').trim().slice(0, 100),
    })
  })
}

// --- Error tracking ---

let errorListenerAttached = false

export function attachErrorTracking(): void {
  if (errorListenerAttached) return
  errorListenerAttached = true

  window.addEventListener('error', (e) => {
    trackEvent('js_error', {
      message: e.message,
      source: e.filename,
      line: e.lineno,
      col: e.colno,
    })
  })

  window.addEventListener('unhandledrejection', (e) => {
    trackEvent('js_error', {
      message: String(e.reason),
      type: 'unhandled_promise',
    })
  })
}

export function isGAInitialized(): boolean {
  return initialized
}
